import { FC, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { deepEqual } from 'fast-equals';
import { useToastContext } from '../../hooks';
import { Loader } from '../../components';
import { DyanmicFieldsModalContent } from './DynamicFieldsModalContent';
import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
  useQuery,
} from '@tanstack/react-query';
import { DynamicFieldAPI } from '../../fetch/dynamic-fields';
import {
  IDynamicField,
  IDynamicFieldDetail,
  IDynamicFieldOption,
} from '../../models/dynamic-fields';
import { ILookupType } from '../../models/employee';
import { IResponse } from '../../models';
// fetch

interface IDynamicFieldsModal {
  open: boolean;
  onClose: () => void;
  selectedDynamicFieldId: string;
  employeeId?: string;
  selectedType: string;
  selectedTeam: string;
  refetchDynamicFieldsData: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<IResponse<IDynamicField[]>, Error>>;
  selectedTypeId: string | number;
  selectedTeamId: string | number;
}

const Schema = Yup.object().shape({
  teamId: Yup.number(),
  name: Yup.string().required('Name is required.'),
  dataType: Yup.string().required('Input type is required.'),
  options: Yup.array()
    .of(
      Yup.object().shape({
        optionId: Yup.mixed(),
        text: Yup.string().test(
          'contains-invalid-characters',
          () => `Options cannot contain the following character: |`,
          value => !value?.includes('|')
        ),
      })
    ),
});

export const DynamicFieldsModal: FC<IDynamicFieldsModal> = ({
  open,
  onClose,
  selectedDynamicFieldId,
  employeeId,
  selectedType,
  selectedTeam,
  refetchDynamicFieldsData,
  selectedTypeId,
  selectedTeamId,
}) => {
  const isEdit = selectedDynamicFieldId !== 'new' && !!selectedDynamicFieldId;
  const { showToast } = useToastContext();

  const [optionsList, setOptionsList] = useState<IDynamicFieldOption[]>([]);
  const { isLoading: isLoadingDynamicField, data: dynamicField } = useQuery<
    IDynamicFieldDetail,
    Error
  >({
    queryKey: ['dynamicField', open, selectedDynamicFieldId],
    queryFn: async () => {
      const data = await DynamicFieldAPI.getDynamicField(selectedDynamicFieldId);
      return data;
    },
    onError(error: any) {
      showToast('error', error?.detail || 'Error trying to load entity types. Please try again.');
    },
    onSuccess(data: any) {
      setOptionsList(data.options);
    },
    enabled: isEdit && open,
  });

  const { isLoading: isLoadingInputTypes, data: inputTypes } = useQuery<ILookupType[], Error>({
    queryKey: ['inputTypes'],
    queryFn: async () => {
      const data = await DynamicFieldAPI.fetchInputTypes();
      return data;
    },
    onError(error: any) {
      showToast('error', error?.detail || 'Error trying to load input types. Please try again.');
    },
  });

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        dynamicFieldId: dynamicField ? dynamicField.dynamicFieldId : '',
        name: dynamicField ? dynamicField.name : '',
        teamId: dynamicField ? dynamicField.teamId : selectedTeamId,
        entityType: dynamicField ? dynamicField.entityType : selectedType,
        dataType: dynamicField ? dynamicField.dataType : inputTypes?.[0].value ?? '',
        employeeId: employeeId ? employeeId : '',
        options: dynamicField
          ? dynamicField.options
          : [{ optionId: 'new-' + new Date().getTime(), text: '' }],
      }}
      validationSchema={Schema}
      validateOnChange={false}
      onSubmit={async (values, actions) => {
        const data = {
          ...values,
          employeeId: employeeId,
          options:
            values.dataType === 1 || values.dataType === 2
              ? values.options.map(o =>
                  o?.optionId?.toString()?.includes('new')
                    ? { ...o, optionId: null, text: o.text ? o.text : 'No value provided' }
                    : o
                )
              : [],
        };

        try {
          if (isEdit) {
            await DynamicFieldAPI.updateDynamicField(data);
          } else {
            await DynamicFieldAPI.createDynamicField(data);
          }

          await refetchDynamicFieldsData();
          actions.resetForm();
        } catch (error: any) {
          const errorMessage =
            error?.errors?.Options?.[0] ?? 'Error saving Dynamic Field, please try again.';
          showToast('error', errorMessage);
        } finally {
          onClose();
          actions.resetForm();
        }
      }}
    >
      {({
        resetForm,
        isSubmitting,
        values,
        setFieldValue,
        handleSubmit,
        dirty,
        isValid,
        initialValues,
        touched,
        errors,
        handleChange,
        handleBlur,
      }) => {
        const handleClose = () => {
          if (!deepEqual(initialValues, values)) {
            const result = window.confirm(
              'You have unsaved changes, are you sure you want to exit?'
            );
            if (result) {
              resetForm();

              onClose();
            } else {
              return;
            }
          } else {
            onClose();
          }
        };

        return open && (!isLoadingDynamicField || !isEdit) ? (
          <DyanmicFieldsModalContent
            open={open}
            handleClose={handleClose}
            setFieldValue={setFieldValue}
            values={values}
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            dirty={dirty}
            isValid={isValid}
            dynamicField={dynamicField}
            isLoadingInputTypes={isLoadingInputTypes}
            inputTypes={inputTypes}
            optionsList={optionsList}
            setOptionsList={setOptionsList}
            selectedType={selectedType}
            selectedTeam={selectedTeam}
            isEdit={isEdit}
          />
        ) : (
          open && isLoadingDynamicField && <Loader type="overlay" position="centered" />
        );
      }}
    </Formik>
  );
};
